import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Copy, Share2 } from "lucide-react";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import {
  selectDineInSession,
  selectDineInMembers,
  acknowledgeCodeRevealed,
} from "../../../../dataManagement/uiDb/DineInDb";
import { useNotification } from "../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../constants/Enums";
import { shareDineInInviteLink } from "../../../../services/helpers/DineInShareHelper";
import DineInTableRoster from "./DineInTableRoster";
import "./styles/DineInCodeReveal.css";

interface DineInCodeRevealProps {
  businessName: string;
}

/**
 * Shown once, right after openTable creates a brand new session for this
 * table (sent sessionCode: null, got success back). No dedicated backend
 * action for "done reading the code out" — the continue button is a local
 * phase transition since the session data is already fully loaded.
 */
const DineInCodeReveal: React.FC<DineInCodeRevealProps> = ({ businessName }) => {
  const dispatch = useDispatch<AppDispatch>();
  const session = useSelector(selectDineInSession);
  const members = useSelector(selectDineInMembers);
  const { triggerNotification } = useNotification();
  const [copied, setCopied] = useState(false);

  if (!session) return null;

  const startedAt = session.createdAt
    ? new Date(session.createdAt).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(session.sessionCode);
      setCopied(true);
      triggerNotification("Code copied!", NotificationType.SUCCESS);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      triggerNotification("Couldn't copy the code.", NotificationType.ERROR);
    }
  };

  // Native share sheet (or clipboard fallback) with the whole join link —
  // whoever opens it skips typing the code entirely (see DineIn.tsx).
  const handleShareLink = async () => {
    const result = await shareDineInInviteLink(session.sessionCode, businessName);
    if (result === "copied") {
      triggerNotification("Invite link copied!", NotificationType.SUCCESS);
    } else if (result === "error") {
      triggerNotification("Couldn't share the link.", NotificationType.ERROR);
    }
  };

  return (
    <div className="dine-in-code-reveal">
      {startedAt && (
        <p className="dine-in-code-reveal__meta">
          TABLE {session.tableNumber} &middot; started {startedAt}
        </p>
      )}
      <h1 className="dine-in-code-reveal__title">
        Read this out <span>to the table</span>
      </h1>

      <div className="dine-in-code-reveal__card">
        <span className="dine-in-code-reveal__label">Secret code</span>
        <div className="dine-in-code-reveal__code">{session.sessionCode}</div>
        <div className="dine-in-code-reveal__card-actions">
          <button className="dine-in-code-reveal__copy-btn" onClick={handleCopy}>
            <Copy size={14} strokeWidth={2} />
            {copied ? "Copied" : "Tap to copy"}
          </button>
          <button className="dine-in-code-reveal__share-btn" onClick={handleShareLink}>
            <Share2 size={14} strokeWidth={2} />
            Share link
          </button>
        </div>
      </div>

      <DineInTableRoster members={members} hostId={session.creatorId} />

      <p className="dine-in-code-reveal__footer">
        Everyone orders on their own phone. One bill, split at the end.
      </p>

      <div className="dine-in-code-reveal__actions">
        <button
          className="dine-in-code-reveal__continue-btn"
          onClick={() => dispatch(acknowledgeCodeRevealed())}
        >
          Continue to the table
        </button>
      </div>
    </div>
  );
};

export default DineInCodeReveal;
